/**
 * "Choose options" flyout — the two behaviors that cannot be expressed as
 * attributes on the panel.
 *
 * The rest of the dialog is declared in Liquid: the panels carry
 * `data-a11y-dialog` to opt into the shared primitive's modal semantics and
 * `x-effect` engages `trapFocusWhenReady` when their open flag flips (see
 * `snippets/card-product.liquid`, `snippets/card-product-shop-the-look.liquid`,
 * `sections/quick-view.liquid`).
 *
 * 1. Opener tracking. The desktop panel sits inside `.card-product` and its
 *    close handlers walk up to the card for the trigger; the shared mobile
 *    popup in `sections/quick-view.liquid` sits outside every card, where that
 *    lookup is `null`. One delegated listener records the opener for both,
 *    rather than threading it through ten trigger call sites.
 *
 * 2. Trap handoff around the mini-cart. Adding to cart opens the drawer over
 *    the still-open flyout; the primitive holds one trap at a time, so the
 *    drawer takes it and, on close, restores focus without knowing the flyout
 *    is there — leaving a panel that looks and reads modal but lets Tab walk
 *    out of it. This re-engages the flyout's trap on `mini-cart:visibility`.
 *
 * The handoff is not solved by giving the primitive a stack of traps: that
 * would change nesting behavior for every other overlay sharing the store, and
 * only these two surfaces need it.
 */
(() => {
  'use strict';

  /** Panels that opted into the flyout's dialog treatment. */
  const PANEL_SELECTOR = '[data-choose-options-dialog]';

  /**
   * The control that opened the flyout most recently. Not cleared on close —
   * the close handlers read it *while* closing, and it is only ever used to
   * restore focus to a trigger still on the page.
   */
  let opener = null;

  /**
   * The flyout that was on screen when the mini-cart took over, waiting to have
   * its trap restored when the cart goes away.
   */
  let interrupted = null;

  const focusStore = () =>
    window.Alpine && typeof window.Alpine.store === 'function'
      ? window.Alpine.store('xFocusElement')
      : null;

  /**
   * Falls back to a connectedness check if the primitive has not published its
   * predicate yet — guessing wrong engages a trap on a hidden panel.
   */
  const isRendered = (el) => {
    const fn = window.FarmacyA11y && window.FarmacyA11y.isRendered;
    if (typeof fn === 'function') return fn(el);
    return !!(el && el.isConnected);
  };

  /**
   * The flyout panel currently on screen. `showOptions` is per-card state and
   * nothing closes card A's panel when card B's opens, so the panel containing
   * focus wins — that is the one the user is in.
   */
  const openPanel = () => {
    const panels = Array.from(document.querySelectorAll(PANEL_SELECTOR));
    if (!panels.length) return null;

    const active = document.activeElement;
    if (active) {
      const focused = panels.find((panel) => panel.contains(active) && isRendered(panel));
      if (focused) return focused;
    }

    return panels.find(isRendered) || null;
  };

  /**
   * Record the opener. Capture phase, so it lands before the trigger's own
   * Alpine handler starts loading the panel.
   *
   * Triggers are identified structurally, not by class: a `[data-id]` whose
   * value resolves to a `#choose-options-*` element is a flyout trigger, and
   * that element exists in the markup whether or not it has ever been opened.
   */
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const trigger = target.closest('[data-id]');
      if (!trigger || !trigger.dataset.id) return;
      if (!document.getElementById(`choose-options-${trigger.dataset.id}`)) return;

      opener = trigger;
    },
    true
  );

  /**
   * ⚠ The drawer dispatches this from `toggleVisibility()` *before* it releases
   * its trap and restores focus, so both branches defer a frame and read the
   * settled state rather than racing it.
   */
  document.addEventListener('mini-cart:visibility', (event) => {
    const visible = !!(event.detail && event.detail.visible);

    if (visible) {
      interrupted = openPanel();
      return;
    }

    const panel = interrupted;
    interrupted = null;
    if (!panel) return;

    requestAnimationFrame(() => {
      // Dismissed while the cart was open (resize, or a second add-to-cart).
      if (!panel.isConnected || !isRendered(panel)) return;

      const store = focusStore();
      if (!store || typeof store.trapFocusWhenReady !== 'function') return;

      // Focus lands on the close button, not the Add-to-cart button the drawer
      // restored to: it is where focus starts every other time this dialog
      // opens, and the one control in the panel carrying a plain `:focus`
      // indicator — `:focus-visible` suppresses the ring on programmatic focus
      // following a pointer interaction, which is exactly this path.
      //
      // Named explicitly; a null degrades to the primitive's first-focusable
      // fallback.
      store.trapFocusWhenReady(panel, panel.querySelector('.group-close-btn'));
    });
  });

  /**
   * Read by the mobile popup's close handlers in `sections/quick-view.liquid`,
   * which have no card to walk up to.
   */
  window.FarmacyA11y = Object.assign(window.FarmacyA11y || {}, {
    chooseOptions: {
      opener: () => opener
    }
  });
})();
